import React, { useEffect, useState } from "react";
import useWorkflowVersions, {
  WorkflowVersionMeta
} from "@/hooks/useWorkflowVersions";

interface Props {
  workflowId: number | string;
  onRestore: (nodesJson: string, versionLabel: string) => void;
  onClose: () => void;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatSavedAt(savedAt: string): { primary: string; secondary: string } {
  // savedAt arrives as "2026-02-28 06:05:56 UTC"
  const date = new Date(savedAt.replace(" UTC", "Z"));
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yestStart = new Date(todayStart);
  yestStart.setDate(yestStart.getDate() - 1);
  const versionDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  const timeStr = date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit"
  });

  if (versionDay.getTime() === todayStart.getTime()) {
    return { primary: "Today", secondary: timeStr };
  }
  if (versionDay.getTime() === yestStart.getTime()) {
    return { primary: "Yesterday", secondary: timeStr };
  }
  return {
    primary: date.toLocaleDateString([], { month: "short", day: "numeric" }),
    secondary: timeStr
  };
}

function shortEmail(email: string): string {
  if (!email) return "Unknown";
  return email.split("@")[0];
}

type BadgeType = "published" | "draft" | "autosave";

function TypeBadge({ type }: { type: BadgeType }) {
  const styles: Record<BadgeType, string> = {
    published: "bg-green-50 text-green-700 border border-green-200",
    draft: "bg-amber-50 text-amber-700 border border-amber-200",
    autosave: "bg-slate-100 text-slate-500 border border-slate-200"
  };
  const labels: Record<BadgeType, string> = {
    published: "Published",
    draft: "Draft",
    autosave: "Autosave"
  };
  return (
    <span
      className={`text-xs font-medium px-2 py-0.5 rounded-full ${styles[type]}`}
    >
      {labels[type]}
    </span>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function WorkflowVersionHistory({
  workflowId,
  onRestore,
  onClose
}: Props) {
  const { versions, loading, error, loadVersions, getVersion } =
    useWorkflowVersions(workflowId);
  const [restoringId, setRestoringId] = useState<number | null>(null);

  useEffect(() => {
    loadVersions();
  }, [loadVersions]);

  async function handleRestore(v: WorkflowVersionMeta) {
    setRestoringId(v.id);
    try {
      const detail = await getVersion(v.id);
      if (!detail?.nodesJson) {
        alert("Version data could not be loaded.");
        return;
      }
      const { primary, secondary } = formatSavedAt(v.savedAt);
      onRestore(detail.nodesJson, `${primary} at ${secondary}`);
    } finally {
      setRestoringId(null);
    }
  }

  const badgeType = (v: WorkflowVersionMeta): BadgeType =>
    v.isAutosave ? "autosave" : v.isPublished ? "published" : "draft";

  // Group: latest manual save vs rest
  const manualVersions = versions.filter((v) => !v.isAutosave);
  const autosaveVersions = versions.filter((v) => v.isAutosave);

  return (
    <div className="absolute top-0 right-0 h-full w-96 bg-white border-l border-slate-200 shadow-xl z-30 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50 flex-shrink-0">
        <div>
          <h3 className="text-sm font-semibold text-slate-800">
            Version History
          </h3>
          <p className="text-xs text-slate-400 mt-0.5">
            Restore loads a version into the editor — save to keep it.
          </p>
        </div>
        <button
          aria-label="Close version history"
          onClick={onClose}
          className="text-slate-400 hover:text-slate-600 p-1.5 rounded focus:outline-none focus:ring-2 focus:ring-cornflower-blue-400"
        >
          ✕
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto">
        {loading && (
          <div className="p-6 text-sm text-slate-500 text-center">
            Loading versions…
          </div>
        )}
        {!loading && error && (
          <div className="p-4 text-sm text-red-600">{error}</div>
        )}
        {!loading && !error && versions.length === 0 && (
          <div className="p-6 text-sm text-slate-500 text-center">
            No saved versions yet.
            <br />
            <span className="text-xs text-slate-400 mt-1 block">
              Versions are created each time you save, publish, or autosave.
            </span>
          </div>
        )}

        {/* Manual saves section */}
        {!loading && manualVersions.length > 0 && (
          <div>
            <div className="px-4 py-2 bg-slate-50 border-b border-slate-100">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                Saved &amp; Published
              </span>
            </div>
            {manualVersions.map((v, idx) => (
              <VersionRow
                key={v.id}
                version={v}
                isLatest={idx === 0 && autosaveVersions.length === 0}
                badgeType={badgeType(v)}
                restoringId={restoringId}
                onRestore={handleRestore}
              />
            ))}
          </div>
        )}

        {/* Autosave section */}
        {!loading && autosaveVersions.length > 0 && (
          <div>
            <div className="px-4 py-2 bg-slate-50 border-b border-slate-100">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                Autosaves{" "}
                <span className="font-normal normal-case text-slate-400">
                  (last {autosaveVersions.length})
                </span>
              </span>
            </div>
            {autosaveVersions.map((v, idx) => (
              <VersionRow
                key={v.id}
                version={v}
                isLatest={idx === 0 && manualVersions.length === 0}
                badgeType="autosave"
                restoringId={restoringId}
                onRestore={handleRestore}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Version Row ───────────────────────────────────────────────────────────────

function VersionRow({
  version: v,
  isLatest,
  badgeType,
  restoringId,
  onRestore
}: {
  version: WorkflowVersionMeta;
  isLatest: boolean;
  badgeType: BadgeType;
  restoringId: number | null;
  onRestore: (v: WorkflowVersionMeta) => void;
}) {
  const { primary, secondary } = formatSavedAt(v.savedAt);
  const isRestoring = restoringId === v.id;

  // Dot color by type
  const dotColor =
    badgeType === "published"
      ? "bg-green-500"
      : badgeType === "draft"
      ? "bg-amber-400"
      : "bg-slate-300";

  return (
    <div className="px-4 py-3 border-b border-slate-100 hover:bg-slate-50 transition-colors">
      <div className="flex items-start gap-3">
        {/* Timeline dot */}
        <div className="flex flex-col items-center pt-1 flex-shrink-0">
          <div className={`w-2.5 h-2.5 rounded-full ${dotColor}`} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Time + badges */}
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="text-sm font-semibold text-slate-800">
              {primary}
            </span>
            <span className="text-sm text-slate-400">{secondary}</span>
            <TypeBadge type={badgeType} />
            {isLatest && (
              <span className="text-xs font-medium bg-cornflower-blue-50 text-cornflower-blue-700 border border-cornflower-blue-200 px-2 py-0.5 rounded-full">
                Latest
              </span>
            )}
          </div>

          {/* Metadata row */}
          <div className="flex items-center gap-3 text-xs text-slate-400">
            <span title={v.savedBy}>
              by {shortEmail(v.savedBy)}
            </span>
            {v.nodeCount > 0 && (
              <>
                <span className="text-slate-200">·</span>
                <span>{v.nodeCount} node{v.nodeCount !== 1 ? "s" : ""}</span>
              </>
            )}
            {v.triggerType && (
              <>
                <span className="text-slate-200">·</span>
                <span className="truncate max-w-24" title={v.triggerType}>
                  {v.triggerType}
                </span>
              </>
            )}
          </div>
        </div>

        {/* Restore button */}
        <button
          onClick={() => onRestore(v)}
          disabled={!!restoringId}
          className={`flex-shrink-0 flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-md border transition-colors focus:outline-none focus:ring-2 focus:ring-cornflower-blue-400 ${
            isRestoring
              ? "bg-slate-100 text-slate-400 border-slate-200 cursor-wait"
              : "bg-white text-cornflower-blue-600 border-cornflower-blue-200 hover:bg-cornflower-blue-50 hover:border-cornflower-blue-300"
          }`}
          aria-label={`Restore version from ${primary} at ${secondary}`}
        >
          {isRestoring ? (
            "Loading…"
          ) : (
            <>
              <svg
                viewBox="0 0 24 24"
                width="12"
                height="12"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                <path d="M3 3v5h5" />
              </svg>
              Restore
            </>
          )}
        </button>
      </div>
    </div>
  );
}
